import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  { value: 40, suffix: "%", label: "Faster Lead Response" },
  { value: 3, suffix: "×", label: "More Conversions" },
  { value: 60, suffix: "%", label: "Less Manual Work" },
  { value: 500, suffix: "+", label: "Real Estate Teams" },
];

const LOGOS = [];

function AnimatedCounter({ value, suffix, duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const end = value;
    const startTime = performance.now();
    const ms = duration * 1000;

    const step = (now) => {
      const progress = Math.min((now - startTime) / ms, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [inView, value, duration]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export function StatsMarquee() {
  return (
    <section className="relative z-10 border-y border-white/5 bg-white/[0.01] py-8 overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-6 md:grid-cols-4 md:gap-8 mb-12"
        >
          {STATS.map((stat) => (
            <motion.div
              key={stat.label}
              variants={itemVariants}
              className="text-center border-r last:border-r-0 border-white/10 px-4 flex flex-col justify-center"
            >
              <span className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-b from-white to-[#4B73FF] md:text-4xl leading-tight">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </span>
              <span className="text-[12px] font-semibold text-white/50 uppercase tracking-wider mt-1.5">
                {stat.label}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {LOGOS.length > 0 && (
        <div className="border-t border-white/5 pt-8 pb-4">
          <p className="text-center tracking-widest uppercase text-white/30 font-semibold mb-6 text-[11px]">
            Trusted by leading real estate developers & agencies
          </p>

          <div className="relative flex overflow-x-hidden">
            <div className="animate-marquee whitespace-nowrap flex gap-16 py-2 text-white/40 text-[16px] font-medium tracking-wide">
              {LOGOS.map((logo, idx) => (
                <span key={`l1-${idx}`} className="hover:text-white/70 transition-colors cursor-default">
                  {logo}
                </span>
              ))}
            </div>

            <div className="absolute top-0 animate-marquee2 whitespace-nowrap flex gap-16 py-2 text-white/40 text-[16px] font-medium tracking-wide">
              {LOGOS.map((logo, idx) => (
                <span key={`l2-${idx}`} className="hover:text-white/70 transition-colors cursor-default">
                  {logo}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes marquee2 {
          0% { transform: translateX(100%); }
          100% { transform: translateX(0%); }
        }
        .animate-marquee {
          animation: marquee 35s linear infinite;
        }
        .animate-marquee2 {
          animation: marquee2 35s linear infinite;
        }
      `}</style>
    </section>
  );
}
